// Typed-name signature with explicit e-sign consent, ported from the Upstream Portal's SignDocument.tsx
// (disclosure block, "Type your full legal name", consent checkbox, Sign / Decline). Used by the firm's signing page
// and the countersigner's dialog on the review. Consent is never remembered: it is a fresh tick every time.
import { useId, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { ESIGN_CONSENT, type SignAgreementRequest } from '@upm/domain';
import { Banner, Button, TextField } from '../ui';

export type SignatureValue = Pick<SignAgreementRequest, 'typedName' | 'title' | 'consent' | 'consentVersion'>;

export function SignatureForm({ defaultName, onSign, busy, error, signLabel = 'Sign', secondary }: {
  defaultName?: string;
  onSign: (v: SignatureValue) => void;
  busy: boolean;
  error: string | null;
  signLabel?: string;
  /** Rendered left of the Sign button (Decline, Cancel). */
  secondary?: React.ReactNode;
}) {
  // Not pre-filled from the invite: the signer types their own name (the portal's legal premise).
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [consent, setConsent] = useState(false);
  const discId = useId();
  const ready = name.trim().length >= 2 && consent && !busy;
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) onSign({ typedName: name.trim(), title: title.trim() || null, consent: true, consentVersion: ESIGN_CONSENT.version });
      }}
    >
      <div className="rounded-xl bg-panel px-4 py-3">
        <div className="section-label mb-1">Electronic signature disclosure</div>
        <p id={discId} className="m-0 text-[13px] leading-[1.55] text-ink-2">{ESIGN_CONSENT.disclosure}</p>
      </div>
      <TextField
        label="Type your full legal name"
        hint={defaultName ? `As it should appear on the agreement, for example ${defaultName}.` : undefined}
        autoComplete="name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="[&_input]:font-serif [&_input]:text-lg [&_input]:italic"
      />
      <TextField label="Title" optional autoComplete="organization-title" placeholder="e.g. Owner" value={title} onChange={(e) => setTitle(e.target.value)} />
      <label className="flex items-start gap-2.5 text-sm leading-[1.5]">
        <input type="checkbox" className="accent-brand mt-0.5 h-[18px] w-[18px] flex-none" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>{ESIGN_CONSENT.checkbox}</span>
      </label>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      <div className="flex flex-wrap-reverse items-center justify-between gap-3">
        <div>{secondary}</div>
        <Button type="submit" variant="primary" size="lg" disabled={!ready}>
          <CheckCircle2 aria-hidden="true" size={18} />
          {busy ? 'Signing…' : signLabel}
        </Button>
      </div>
    </form>
  );
}
